import React, { useState, useEffect } from 'react';
import { Modal, Layout, Typography, Button } from '@douyinfe/semi-ui';
import { IconBarChartVStroked } from '@douyinfe/semi-icons';
import { useTokenQuery } from '../hooks/useTokenQuery';
import TokenInput from './dashboard/TokenInput';
import StatusCards from './dashboard/StatusCards';
import UsageChart from './dashboard/UsageChart';
import LogsList from './dashboard/LogsList';
import { ITEMS_PER_PAGE } from '../constants';

const { Text } = Typography;
const { Content } = Layout;

const getAnnouncementFingerprint = (data) => {
    const items = Array.isArray(data?.items)
        ? data.items.map(item => `${item.question || ''}:${item.answer || ''}`).join('|')
        : '';
    return `${data?.title || ''}|${data?.okText || ''}|${items}`;
};

const LogsTable = () => {
    const {
        apikey,
        setAPIKey,
        loading,
        logsLoading,
        baseUrls,
        activeTabKey,
        handleTabChange,
        fetchData,
        activeTabData,
        logPage,
        logTotal,
        logPageSize,
        handleLogPageChange,
        allLogs,
        allLogsLoading,
        allLogsCooldown,
        loadAllLogs,
    } = useTokenQuery();

    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [dollarCost, setDollarCost] = useState('0.5');
    const [showRMB, setShowRMB] = useState(false);
    const [showChart, setShowChart] = useState(false);

    // 公告逻辑
    const [showAnnouncement, setShowAnnouncement] = useState(false);
    const [announcementData, setAnnouncementData] = useState(null);

    useEffect(() => {
        fetch('/announcement.json?' + Date.now())
            .then(res => res.json())
            .then(data => {
                setAnnouncementData(data);
                if (data.enabled) {
                    const fingerprint = getAnnouncementFingerprint(data);
                    const stored = localStorage.getItem('announcement_dismissed');
                    // stored 格式: "日期|指纹"，日期和指纹都匹配时才不弹
                    if (stored) {
                        const [dismissedDate, dismissedFp] = stored.split('|##|');
                        const today = new Date().toISOString().slice(0, 10);
                        if (dismissedDate === today && dismissedFp === fingerprint) {
                            return; // 今天已关闭且内容没变，不弹
                        }
                    }
                    setShowAnnouncement(true);
                }
            })
            .catch(err => console.log('公告加载失败:', err));
    }, []);

    const renderContent = () => {
        const hasData = activeTabData && activeTabData.tokenValid;

        return (
        <div className="container">
            {!hasData && <h1 className="header-title">API Dashboard</h1>}

            <TokenInput
                apikey={apikey}
                setAPIKey={setAPIKey}
                onSearch={fetchData}
                loading={loading}
                hasData={hasData}
                baseUrls={baseUrls}
                activeTabKey={activeTabKey}
                onTabChange={handleTabChange}
            />

            <StatusCards
                data={activeTabData}
                loading={loading}
            />

            {hasData && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, marginBottom: -8 }}>
                        <Button
                            icon={<IconBarChartVStroked />}
                            theme="light"
                            type="tertiary"
                            loading={allLogsLoading}
                            disabled={allLogsLoading || allLogsCooldown}
                            onClick={async () => {
                                await loadAllLogs();
                                setShowChart(true);
                            }}
                        >
                            {allLogsCooldown ? '冷却中（30s）' : allLogsLoading ? '加载中...' : '消耗统计'}
                        </Button>
                    </div>

                    <LogsList
                        logs={activeTabData.logs || []}
                        loading={logsLoading}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        showRMB={showRMB}
                        dollarCost={dollarCost}
                        serverPage={logPage}
                        serverTotal={logTotal}
                        serverPageSize={logPageSize}
                        onServerPageChange={handleLogPageChange}
                    />

                    <Modal
                        title={`消耗统计（共 ${allLogs.length} 条日志）`}
                        visible={showChart && allLogs.length > 0}
                        onCancel={() => setShowChart(false)}
                        footer={null}
                        centered
                        width={900}
                        bodyStyle={{ padding: '0 16px 16px' }}
                    >
                        <UsageChart
                            logs={allLogs}
                            selectedDate={selectedDate}
                            onDateChange={setSelectedDate}
                            dollarCost={dollarCost}
                            setDollarCost={setDollarCost}
                            showRMB={showRMB}
                            setShowRMB={setShowRMB}
                        />
                    </Modal>
                </>
            )}

            {/* 公告弹窗 */}
            {announcementData && (
                <Modal
                    title={announcementData.title}
                    visible={showAnnouncement}
                    onOk={() => setShowAnnouncement(false)}
                    onCancel={() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const fingerprint = getAnnouncementFingerprint(announcementData);
                        localStorage.setItem('announcement_dismissed', `${today}|##|${fingerprint}`);
                        setShowAnnouncement(false);
                    }}
                    okText={announcementData.okText}
                    cancelText="今日不再提示"
                    centered
                    width={600}
                >
                    <div style={{ lineHeight: 1.8 }}>
                        {announcementData.items && announcementData.items.map((item, index) => (
                            <div key={index} style={{ marginBottom: 16 }}>
                                <Text strong style={{ fontSize: 16, color: '#0071e3' }}>问题{index + 1}：</Text>
                                <div style={{ marginBottom: 8 }}>{item.question}</div>
                                <Text strong style={{ color: '#34c759' }}>解答：</Text>
                                <div style={{ color: '#666' }}>{item.answer}</div>
                            </div>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
    };

    return (
        <Layout style={{ background: 'transparent' }}>
            <Content>
                {renderContent()}
            </Content>
        </Layout>
    );
};

export default LogsTable;

